import type { AxiosInstance } from 'axios';

type Vec3Tuple3d = [number, number, number];
type Vec3Obj3d = { x: number; y: number; z: number };

export interface Printability3dStatus {
  status: 'AWAITING_REQUIREMENTS' | 'PENDING' | 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED';
  assessmentRunId: string | null;
  stageNumber: number | null;
  currentStage: string | null;
  completedAt: string | null;
}

export interface OverhangDistribution3d {
  selfSupportingPct: number;
  moderatePct: number;
  severePct: number;
  downfacingPct: number;
  supportAreaPct: number;
  maxOverhangAngleDeg: number;
  meanOverhangAngleDeg: number;
}

export interface OrientationOption3d {
  upDirectionModel: Vec3Tuple3d;
  rotationToPrinterZ?: { w: number; x: number; y: number; z: number };
  score: number;
  contactAreaMm2: number;
  overhangAreaMm2: number;
}

export type ProcessCategory3d =
  | 'material_extrusion'
  | 'vat_photopolymerization'
  | 'powder_bed_fusion_polymer'
  | 'powder_bed_fusion_metal'
  | 'material_jetting'
  | 'binder_jetting';

export type MaterialFamily3d =
  | 'metal'
  | 'polymer'
  | 'ceramic'
  | 'composite'
  | 'sand'
  | 'wax'
  | 'other';

export type Confidence3d = 'low' | 'medium' | 'high';

export interface MaterialCandidate3d {
  materialId: number;
  name: string | null;
  manufacturer: string | null;
  amProcess: string | null;
  buildMaterialType: string | null;
  family: MaterialFamily3d;
  tensileStrengthMinMpa: number | null;
  tensileStrengthMaxMpa: number | null;
  score: number;
  reasons: string[];
}

export interface EquipmentCandidate3d {
  equipmentId: string | null;
  manufacturer: string | null;
  model: string | null;
  amProcess: string | null;
  buildVolumeMm: { x: number | null; y: number | null; z: number | null };
  focusDiameterMinMicrons: number | null;
  score: number;
  reasons: string[];
}

export interface ProcessCandidate3d {
  process: ProcessCategory3d;
  score: number;
  criticalAngleDeg: number;
  confidence: Confidence3d;
  disqualified: boolean;
  reasons: string[];
  materials: MaterialCandidate3d[];
}

export interface CombinationCandidate3d {
  process: ProcessCategory3d;
  criticalAngleDeg: number;
  material: MaterialCandidate3d;
  equipment: EquipmentCandidate3d;
  score: number;
  confidence: Confidence3d;
  reasons: string[];
}

export interface RecommendationSection3d {
  rulesVersion: string;
  senvolSnapshotVersion: string | null;
  informedByRequirements: boolean;
  top: {
    process: ProcessCategory3d;
    materialId: number | null;
    equipmentId: string | null;
    criticalAngleDeg: number;
    confidence: Confidence3d;
  } | null;
  combos: CombinationCandidate3d[];
  candidates: ProcessCandidate3d[];
}

export type PostProcessNecessity3d = 'required' | 'recommended' | 'optional';
export type PostProcessCategory3d =
  | 'cleaning'
  | 'support'
  | 'surface'
  | 'thermal'
  | 'machining'
  | 'coating'
  | 'assembly';

export interface PostProcessStep3d {
  step: string;
  category: PostProcessCategory3d;
  necessity: PostProcessNecessity3d;
  reason: string;
}

export interface PostProcessingSection3d {
  process: ProcessCategory3d | null;
  steps: PostProcessStep3d[];
}

export type ChecklistItemStatus3d = 'done' | 'warning' | 'skipped';
export interface ChecklistItem3d {
  step: string;
  status: ChecklistItemStatus3d;
  detail: string;
}

export interface Model3dAnalysisData {
  schemaVersion: number;
  mesh: {
    sourceFormat: 'stl' | 'step' | 'iges';
    previewUrl?: string;
    thumbnailUrl?: string;
    triangleCount: number;
    vertexCount: number;
    tessellationDeflectionMm: number;
    units: {
      source: 'mm' | 'inch' | 'unknown';
      scaleToMm: number;
      confidence: 'detected' | 'assumed';
    };
  };
  geometry: {
    triangleCount: number;
    vertexCount: number;
    dimensionsMm: Vec3Obj3d;
    boundingBox: { minMm: Vec3Tuple3d; maxMm: Vec3Tuple3d };
    minDimensionMm: number;
    maxDimensionMm: number;
    aspectRatio: number;
    volumeMm3: number;
    surfaceAreaMm2: number;
    surfaceAreaToVolumeRatio: number;
    boundingBoxFillRatio: number;
    centerOfMassMm: Vec3Obj3d;
    isWatertight: boolean;
    isManifold: boolean;
    boundaryEdgeCount: number;
    nonManifoldEdgeCount: number;
    bodyCount: number;
    wallThickness: {
      minMm: number;
      p5Mm: number;
      meanMm: number;
      medianMm: number;
      maxMm: number;
      thinRegionPct: number;
      reliable: boolean;
      hitRatePct: number;
    };
    hasBrepTopology: boolean;
    solidCount: number;
    shellCount: number;
    internalCavityCount: number;
    holes: Array<{ diameterMm: number }>;
  };
  orientation: {
    criticalAngleDeg?: number;
    recommended: OrientationOption3d;
    candidates: OrientationOption3d[];
    overhang: OverhangDistribution3d;
  };
  recommendation?: RecommendationSection3d;
  postProcessing?: PostProcessingSection3d;
  checklist?: ChecklistItem3d[];
  aiInsight?: string;
}

export interface Printability3dResult {
  id: string;
  assessmentRunId: string;
  partId: string;
  outcome: 'complete' | 'failed';
  failureReason: string | null;
  schemaVersion: number;
  orientationScore: number | null;
  volumeMm3: number | null;
  surfaceAreaMm2: number | null;
  minWallThicknessMm: number | null;
  wallThicknessReliable: boolean | null;
  isWatertight: boolean | null;
  bodyCount: number | null;
  solidCount: number | null;
  triangleCount: number | null;
  hasBrepTopology: boolean | null;
  analysis: Model3dAnalysisData | null;
  requirements?: FunctionalRequirements | null;
  createdAt: string;
  updatedAt: string;
}

export interface FunctionalRequirements {
  materialType?: 'metal' | 'plastic';
  loadBearingDetails?: string;
  temperatureDetails?: string;
  load?: 'none' | 'light' | 'structural';
  serviceTempC?: number;
  flexibility?: 'rigid' | 'semi' | 'flexible';
  environment?: Array<'indoor' | 'uv' | 'chemical' | 'moisture'>;
  transparency?: 'opaque' | 'translucent' | 'clear';
  biocompatible?: boolean;
  foodSafe?: boolean;
  cosmeticFinish?: 'functional' | 'show' | 'paintable';
  toleranceGrade?: 'standard' | 'tight' | 'precision';
  certifications?: string[];
  finishingTreatment?: string;
  surfaceRoughness?: string;
  inserts?: string;
  stringentRequirements?: string[];
}

export interface RequirementField {
  id: string;
  label: string;
  type: 'enum' | 'number' | 'boolean' | 'multiselect';
  options?: string[];
  unit?: string;
  whyItMatters: string;
}

export interface RequirementsPrompt {
  fields: RequirementField[];
}

export interface Printability3dReference {
  processes: Array<{ category: ProcessCategory3d; label: string; description: string }>;
  materialFamilies: Array<{ family: MaterialFamily3d; description: string }>;
}

export interface Printability3dRequirements {
  prompt: RequirementsPrompt;
  current: FunctionalRequirements | null;
  requested: boolean;
}

export interface SubmitPrintability3dRequirementsRequest {
  requirements?: FunctionalRequirements;
  proceedWithout?: boolean;
}

export interface SubmitPrintability3dRequirementsResult {
  assessmentRunId: string;
  status: Printability3dStatus['status'];
}

export interface Printability3dService {
  getReference: () => Promise<Printability3dReference>;
  getStatus: (partId: string) => Promise<Printability3dStatus>;
  getLatest: (partId: string) => Promise<Printability3dResult | null>;
  getRequirements: (partId: string) => Promise<Printability3dRequirements>;
  submitRequirements: (
    partId: string,
    body: SubmitPrintability3dRequirementsRequest,
  ) => Promise<SubmitPrintability3dRequirementsResult>;
}

export function createPrintability3dService(client: AxiosInstance): Printability3dService {
  return {
    getReference: async () => {
      const { data } = await client.get<Printability3dReference>('/printability-3d/reference');
      return data;
    },
    getStatus: async (partId: string) => {
      const { data } = await client.get<Printability3dStatus>(
        `/printability-3d/parts/${partId}/status`,
      );
      return data;
    },
    getLatest: async (partId: string) => {
      const { data } = await client.get<Printability3dResult | null>(
        `/printability-3d/parts/${partId}/latest`,
      );
      return data;
    },
    getRequirements: async (partId: string) => {
      const { data } = await client.get<Printability3dRequirements>(
        `/printability-3d/parts/${partId}/requirements`,
      );
      return data;
    },
    submitRequirements: async (partId: string, body: SubmitPrintability3dRequirementsRequest) => {
      const { data } = await client.post<SubmitPrintability3dRequirementsResult>(
        `/printability-3d/parts/${partId}/requirements`,
        body,
      );
      return data;
    },
  };
}
